import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAiChatStore, type ChatMsg } from './aiChatStore';
import { useAiAvailabilityStore } from './aiAvailabilityStore';
import { aiApi, type AiHealth, type AiMessage, type AiRating } from '../api/ai';

function toChatMsg(m: AiMessage): ChatMsg {
  return {
    key: m.id,
    id: m.id,
    role: m.role,
    content: m.content,
    sources: m.sources ?? null,
    suggestions: m.suggestions ?? null,
    rating: m.rating ?? null,
    streaming: false,
  };
}

export function useAiChats(active: boolean) {
  const store = useAiChatStore;

  const refresh = useCallback(() => {
    store.getState().setLoadingChats(true);
    aiApi
      .listChats()
      .then((r) => store.getState().setChats(r.data))
      .catch(() => undefined)
      .finally(() => store.getState().setLoadingChats(false));
  }, [store]);

  const openSeq = useRef(0);
  const open = useCallback(
    (chatId: string) => {
      const mySeq = ++openSeq.current;
      aiApi
        .messages(chatId)
        .then((r) => {
          if (mySeq === openSeq.current) store.getState().loadChat(chatId, r.data.map(toChatMsg));
        })
        .catch(() => {
          if (mySeq === openSeq.current) toast.error('Не удалось открыть чат');
        });
    },
    [store],
  );

  const newChat = useCallback(() => {
    openSeq.current += 1;
    store.getState().reset();
  }, [store]);

  const rate = useCallback(
    (messageId: string, rating: AiRating | null, reason?: string) => {
      store.getState().setRating(messageId, rating);
      aiApi.rate(messageId, rating, reason).catch(() => toast.error('Не удалось сохранить оценку'));
    },
    [store],
  );

  useEffect(() => {
    if (!active) return;
    refresh();
    const sync = (l: AiHealth['load'] | null) => {
      if (l) store.getState().setLoad(l);
    };
    sync(useAiAvailabilityStore.getState().load);
    const unsub = useAiAvailabilityStore.subscribe((s) => sync(s.load));
    return unsub;
  }, [active, refresh, store]);

  return { refresh, open, newChat, rate };
}
